import { useEffect, useState } from 'react';
import { Alert, api, Badge, Button, fmtMoney, Input, ListPage, Modal, Select, useFetch, useForm, usePagedList } from '@greenrinse/ui';
import type { Partner } from './PartnersPage';
import type { Site } from './SitesPage';

const TYPES = ['Sedan', 'Suv', 'Van', 'Pickup', 'Truck', 'Bus', 'Motorcycle'] as const;
const SERVICES = ['Exterior', 'Interior', 'Full'] as const;

export interface PriceRule { vehicleType: string; service: string; amountMinor: number; waterLitres: number | null }
export interface Contract {
  id: string; partnerId: string; partnerName: string; name: string; reference: string | null; status: string; validFrom: string; validTo: string | null; currency: string;
  taxRateBps: number | null; taxInclusive: boolean; frequency: string; customCycleDays: number | null; cycleDays: number; slaCoverageBps: number; minWashDurationSeconds: number;
  duplicatePolicy: string; maxWashesPerCycle: number; unlistedVehiclePolicy: string; disputeWindowDays: number; billMissingInPartnerReportAfterWindow: boolean;
  reconciliationDateWindowDays: number; allowExterior: boolean; allowInterior: boolean; allowFull: boolean; notes: string | null; siteIds: string[]; siteNames: string[]; prices: PriceRule[];
}

type Tone = 'default' | 'success' | 'warning' | 'error' | 'accent';
function statusTone(s: string): Tone { return s === 'Active' ? 'success' : s === 'Suspended' ? 'warning' : s === 'Ended' ? 'error' : 'default'; }

export function ContractForm({ contract, partnerId, onSaved, onCancel }: { contract: Contract | null; partnerId?: string; onSaved: () => void; onCancel: () => void }) {
  const partners = useFetch<{ items: Partner[] }>(contract || partnerId ? null : '/api/partners?pageSize=200');
  const form = useForm({
    partnerId: contract?.partnerId ?? partnerId ?? '', name: contract?.name ?? '', reference: contract?.reference ?? '', status: contract?.status ?? 'Draft',
    validFrom: contract?.validFrom ?? new Date().toISOString().slice(0, 10), validTo: contract?.validTo ?? '', currency: contract?.currency ?? '',
    taxRateBps: contract?.taxRateBps?.toString() ?? '', taxInclusive: contract?.taxInclusive ?? false, frequency: contract?.frequency ?? 'Weekly',
    customCycleDays: contract?.customCycleDays?.toString() ?? '', slaCoverageBps: String(contract?.slaCoverageBps ?? 9500), minWashDurationSeconds: String(contract?.minWashDurationSeconds ?? 180),
    duplicatePolicy: contract?.duplicatePolicy ?? 'BillOnce', maxWashesPerCycle: String(contract?.maxWashesPerCycle ?? 1), unlistedVehiclePolicy: contract?.unlistedVehiclePolicy ?? 'FlagForReview',
    disputeWindowDays: String(contract?.disputeWindowDays ?? 7), billMissingInPartnerReportAfterWindow: contract?.billMissingInPartnerReportAfterWindow ?? true,
    reconciliationDateWindowDays: String(contract?.reconciliationDateWindowDays ?? 1), allowExterior: contract?.allowExterior ?? true, allowInterior: contract?.allowInterior ?? false,
    allowFull: contract?.allowFull ?? false, notes: contract?.notes ?? '',
  });
  const effectivePartner = form.values.partnerId || partners.data?.items[0]?.id || '';
  const partner = partners.data?.items.find((p) => p.id === effectivePartner);
  const sites = useFetch<{ items: Site[] }>(effectivePartner ? `/api/sites?partnerId=${effectivePartner}&pageSize=200` : null, [effectivePartner]);
  const countries = useFetch<{ id: string; currency: string }[]>('/api/countries');
  const [siteIds, setSiteIds] = useState<string[]>(contract?.siteIds ?? []);
  const [prices, setPrices] = useState<Record<string, string>>(() =>
    Object.fromEntries((contract?.prices ?? []).map((p) => [`${p.vehicleType}|${p.service}`, (p.amountMinor / 100).toFixed(2)])));

  // Default the currency from the partner's country.
  useEffect(() => {
    if (!form.values.currency && partner && countries.data) {
      const c = countries.data.find((x) => x.id === partner.countryId);
      if (c) form.set('currency', c.currency);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partner, countries.data]);

  const allowed = SERVICES.filter((s) => (s === 'Exterior' && form.values.allowExterior) || (s === 'Interior' && form.values.allowInterior) || (s === 'Full' && form.values.allowFull));

  const save = () => form.submit(async (v) => {
    const priceRules: PriceRule[] = [];
    for (const [key, val] of Object.entries(prices)) {
      const [vehicleType, service] = key.split('|');
      if (!allowed.includes(service as typeof SERVICES[number])) continue;
      const n = Number(val);
      if (val !== '' && !Number.isNaN(n)) priceRules.push({ vehicleType, service, amountMinor: Math.round(n * 100), waterLitres: null });
    }
    const body = {
      ...v, partnerId: effectivePartner, reference: v.reference || null, validTo: v.validTo || null, taxRateBps: v.taxRateBps === '' ? null : Number(v.taxRateBps),
      customCycleDays: v.frequency === 'Custom' ? Number(v.customCycleDays) : null, slaCoverageBps: Number(v.slaCoverageBps), minWashDurationSeconds: Number(v.minWashDurationSeconds),
      maxWashesPerCycle: Number(v.maxWashesPerCycle), disputeWindowDays: Number(v.disputeWindowDays), reconciliationDateWindowDays: Number(v.reconciliationDateWindowDays),
      notes: v.notes || null, siteIds, prices: priceRules,
    };
    if (contract) await api(`/api/contracts/${contract.id}`, { method: 'PUT', body }); else await api('/api/contracts', { method: 'POST', body });
    onSaved();
  });

  const check = (key: 'taxInclusive' | 'billMissingInPartnerReportAfterWindow' | 'allowExterior' | 'allowInterior' | 'allowFull', label: string) => (
    <label className="gr-row" style={{ marginBottom: 12 }}><input type="checkbox" checked={form.values[key]} onChange={(e) => form.set(key, e.target.checked)} /> {label}</label>
  );

  return (
    <form onSubmit={(e) => { e.preventDefault(); void save(); }} className="gr-stack">
      {form.message && <Alert tone="error">{form.message}</Alert>}
      <div className="gr-form-grid">
        {!contract && !partnerId && <Select label="Partner" value={effectivePartner} onChange={(e) => form.set('partnerId', e.target.value)} options={(partners.data?.items ?? []).map((p) => ({ value: p.id, label: p.name }))} />}
        <Input label="Contract name" {...form.bind('name')} />
        <Input label="Reference" {...form.bind('reference')} />
        <Select label="Status" {...form.bind('status')} options={['Draft', 'Active', 'Suspended', 'Ended'].map((s) => ({ value: s, label: s }))} />
        <Input label="Currency" maxLength={3} {...form.bind('currency')} />
        <Input label="Valid from" type="date" {...form.bind('validFrom')} />
        <Input label="Valid to (optional)" type="date" {...form.bind('validTo')} />
        <Input label="Tax rate override (bps, blank = country default)" type="number" {...form.bind('taxRateBps')} />
        <div>{check('taxInclusive', 'Prices are tax-inclusive')}</div>
      </div>

      <h3>Covered sites</h3>
      <div className="gr-row">
        {(sites.data?.items ?? []).map((s) => (
          <label key={s.id} className="gr-badge" style={{ cursor: 'pointer', padding: '6px 12px', background: siteIds.includes(s.id) ? 'var(--gr-primary-container)' : undefined }}>
            <input type="checkbox" checked={siteIds.includes(s.id)} onChange={(e) => setSiteIds((ids) => e.target.checked ? [...ids, s.id] : ids.filter((i) => i !== s.id))} /> {s.name}
          </label>
        ))}
        {sites.data && sites.data.items.length === 0 && <span className="gr-muted">This partner has no sites yet.</span>}
      </div>
      {form.errors['siteIds'] && <span className="gr-error-text">{form.errors['siteIds'][0]}</span>}

      <h3>Services & price matrix <span className="gr-muted" style={{ fontWeight: 400, fontSize: 14 }}>({form.values.currency || 'currency'})</span></h3>
      <div className="gr-row">{check('allowExterior', 'Exterior')}{check('allowInterior', 'Interior')}{check('allowFull', 'Full')}</div>
      <div style={{ overflowX: 'auto' }}>
        <table className="gr-table gr-matrix">
          <thead><tr><th>Vehicle type</th>{allowed.map((s) => <th key={s} style={{ textAlign: 'end' }}>{s}</th>)}</tr></thead>
          <tbody>
            {TYPES.map((t) => (
              <tr key={t}><td>{t}</td>{allowed.map((s) => (
                <td key={s} style={{ textAlign: 'end' }}>
                  <input className="gr-input" type="number" min={0} step="0.01" placeholder="—" value={prices[`${t}|${s}`] ?? ''} onChange={(e) => setPrices((p) => ({ ...p, [`${t}|${s}`]: e.target.value }))} />
                </td>
              ))}</tr>
            ))}
          </tbody>
        </table>
      </div>
      {form.errors['prices'] && <span className="gr-error-text">{form.errors['prices'][0]}</span>}

      <h3>Cycle & rules</h3>
      <div className="gr-form-grid">
        <Select label="Wash frequency" {...form.bind('frequency')} options={[{ value: 'Weekly', label: 'Weekly (7-day cycle)' }, { value: 'TwiceMonthly', label: 'Twice a month (15-day cycle)' }, { value: 'Monthly', label: 'Monthly (30-day cycle)' }, { value: 'Custom', label: 'Custom (every N days)' }]} />
        {form.values.frequency === 'Custom' && <Input label="Cycle length (days)" type="number" min={1} {...form.bind('customCycleDays')} />}
        <Input label="SLA coverage (bps, 9500 = 95%)" type="number" {...form.bind('slaCoverageBps')} />
        <Input label="Minimum wash duration (seconds)" type="number" {...form.bind('minWashDurationSeconds')} />
        <Select label="Duplicate policy" {...form.bind('duplicatePolicy')} options={[{ value: 'BillOnce', label: 'Bill once per cycle, flag extras' }, { value: 'BillAll', label: 'Bill every wash (up to max per cycle)' }, { value: 'Reject', label: 'Reject duplicates' }]} />
        <Input label="Max billable washes per vehicle per cycle (0 = unlimited)" type="number" {...form.bind('maxWashesPerCycle')} />
        <Select label="Unlisted vehicle policy" {...form.bind('unlistedVehiclePolicy')} options={[{ value: 'FlagForReview', label: 'Record, flag, bill after partner accepts' }, { value: 'BillAsRecorded', label: 'Bill as recorded' }, { value: 'Block', label: 'Washer cannot save unlisted vehicles' }]} />
        <Input label="Dispute window (days)" type="number" {...form.bind('disputeWindowDays')} />
        <Input label="Reconciliation date window (± days)" type="number" {...form.bind('reconciliationDateWindowDays')} />
        <div>{check('billMissingInPartnerReportAfterWindow', '"Missing in partner report" becomes billable after the dispute window')}</div>
        <div className="gr-span-2 gr-field"><label>Notes</label><textarea className="gr-textarea" rows={2} value={form.values.notes} onChange={(e) => form.set('notes', e.target.value)} /></div>
      </div>

      <div className="gr-row" style={{ justifyContent: 'flex-end' }}>
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" loading={form.busy}>Save</Button>
      </div>
    </form>
  );
}

export default function ContractsPage({ partnerId }: { partnerId?: string }) {
  const [status, setStatus] = useState('');
  const list = usePagedList<Contract>('/api/contracts', { partnerId, status: status || undefined });
  const [editing, setEditing] = useState<Contract | 'new' | null>(null);

  return (
    <>
      <ListPage<Contract>
        title="Contracts" state={list} rowKey={(c) => c.id} onRowClick={(c) => setEditing(c)}
        actions={<Button onClick={() => setEditing('new')}>Add contract</Button>}
        filters={
          <select className="gr-select" style={{ width: 'auto', minHeight: 40 }} value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {['Draft', 'Active', 'Suspended', 'Ended'].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        }
        columns={[
          { key: 'name', header: 'Contract', render: (c) => <><strong>{c.name}</strong>{c.reference && <span className="gr-muted"> · {c.reference}</span>}{!partnerId && <div className="gr-muted" style={{ fontSize: 13 }}>{c.partnerName}</div>}</> },
          { key: 'period', header: 'Period', render: (c) => `${c.validFrom} → ${c.validTo ?? 'open'}` },
          { key: 'cycle', header: 'Cycle', render: (c) => `${c.frequency} (${c.cycleDays} d)` },
          { key: 'sites', header: 'Sites', render: (c) => c.siteNames.join(', ') || '—' },
          { key: 'prices', header: 'From', render: (c) => c.prices.length ? fmtMoney(Math.min(...c.prices.map((p) => p.amountMinor)), c.currency) : '—', align: 'end' },
          { key: 'status', header: 'Status', render: (c) => <Badge tone={statusTone(c.status)}>{c.status}</Badge> },
        ]}
      />
      <Modal open={editing !== null} wide title={editing === 'new' ? 'New contract' : 'Edit contract'} onClose={() => setEditing(null)}>
        {editing !== null && <ContractForm contract={editing === 'new' ? null : editing} partnerId={partnerId} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); list.reload(); }} />}
      </Modal>
    </>
  );
}
